import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import { Box, Checkbox, Group, Popover } from "@mantine/core";
import { IconChevronDown, IconAlertOctagon } from "@tabler/icons-react";
import clsx from "clsx";

// import { DropdownMenu } from '../Dropdown/DropdownMenu';
// import { DropdownRow } from '../Dropdown/DropdownRow';
// import ActionChip from '../chips/ActionChip';
import styles from "./InputDropdown.module.scss";
import { ActionChip, DropdownMenu, DropdownRow } from "@onengine/component-library";

type Size = "small" | "large";

export type DropdownItem = {
  label: string;
  value: string;
  disabled?: boolean;
  danger?: boolean;
};

type SingleProps = {
  mode?: "single";
  value: string | null;
  onChange: (value: string | null) => void;
};

type MultiProps = {
  mode: "multi";
  value: string[];
  onChange: (values: string[]) => void;
};

export type SharedInputProps = {
  label?: string;
  labelledBy?: string;
  optional?: boolean;
  hint?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: Size;
  error?: boolean | string;
  className?: string;
  style?: CSSProperties;
  items: DropdownItem[];
};

export type InputDropdownProps = (SingleProps | MultiProps) & SharedInputProps;

function isMultiProps(p: SingleProps | MultiProps): p is MultiProps {
  return p.mode === "multi";
}

export default function InputDropdown(props: InputDropdownProps) {
  const {
    label,
    labelledBy,
    optional,
    hint,
    placeholder = "Select..",
    disabled,
    size = "small",
    error,
    className,
    style,
    items,
  } = props;

  const id = useId();
  const labelId = `${id}-label`;
  const [open, setOpen] = useState(false);
  const isMulti = isMultiProps(props);

  useEffect(() => {
    if (!disabled) return;
    setOpen(false);
  }, [disabled]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const selectedItems = useMemo(() => {
    if (isMulti) {
      const selectedSet = new Set(props.value);
      return items.filter((item) => selectedSet.has(item.value));
    }

    const currentValue = props.value ?? null;

    if (!currentValue) return [];

    const found = items.find((item) => item.value === currentValue);
    return found ? [found] : [];
  }, [items, isMulti, props.value]);

  const isChecked = useCallback(
    (val: string) => {
      if (isMulti) {
        return props.value.includes(val);
      }

      return props.value === val;
    },
    [isMulti, props.value],
  );

  const toggleSingle = useCallback(
    (val: string) => {
      if (disabled || isMulti) return;

      const current = props.value ?? null;
      const next = current === val ? null : val;

      props.onChange(next);
      setOpen(false);
    },
    [disabled, isMulti, props],
  );

  const toggleMulti = useCallback(
    (val: string) => {
      if (disabled || !isMulti) return;

      const current = new Set(props.value);

      if (current.has(val)) {
        current.delete(val);
      } else {
        current.add(val);
      }

      props.onChange(Array.from(current));
    },
    [disabled, isMulti, props],
  );

  const handleChipRemove = useCallback(
    (val: string) => {
      if (disabled) return;

      if (isMulti) {
        props.onChange(props.value.filter((v) => v !== val));
        return;
      }

      if (props.value === val) {
        props.onChange(null);
      }
    },
    [disabled, isMulti, props],
  );

  const triggerClass = clsx(
    styles.trigger,
    size === "small" ? styles.triggerSmall : styles.triggerLarge,
    open && styles.triggerOpen,
    disabled && styles.triggerDisabled,
    error && styles.triggerError,
  );

  const valueContent =
    selectedItems.length > 0 ? (
      <Box className={styles.value}>
        {isMulti ? (
          <Group gap={4} wrap="wrap">
            {selectedItems.map((item) => (
              <Box key={item.value} className={styles.dropdownChip}>
                <ActionChip
                  label={item.label}
                  value={item.value}
                  size={size === "large" ? "sm" : "xs"}
                  checked
                  onChange={(checked) => {
                    if (!checked) {
                      handleChipRemove(item.value);
                    }
                  }}
                  onRemove={() => handleChipRemove(item.value)}
                />
              </Box>
            ))}
          </Group>
        ) : (
          <span className={styles.valueText}>{selectedItems[0]?.label}</span>
        )}
      </Box>
    ) : (
      <Box component="span" className={clsx(styles.value, styles.placeholder)}>
        <span className={styles.valueText}>{placeholder}</span>
      </Box>
    );

  return (
    <Popover
      opened={open}
      onChange={setOpen}
      position="bottom-start"
      offset={6}
      shadow="md"
      withinPortal
      middlewares={{ flip: true, shift: true }}
      width="target"
    >
      <Popover.Target>
        <Box className={clsx(styles.wrapper, className)} style={style}>
          {(label || optional) && (
            <Box className={styles.labelRow}>
              {label && (
                <Box component="span" id={labelId} className={styles.labelText}>
                  {label}
                </Box>
              )}
              {optional && <span className={styles.optionalText}>Optional</span>}
            </Box>
          )}

          <Box
            component="button"
            id={id}
            type="button"
            className={triggerClass}
            onClick={() => {
              if (!disabled) {
                setOpen((v) => !v);
              }
            }}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-labelledby={
              label ? `${labelId} ${id}` : labelledBy ? `${labelledBy} ${id}` : undefined
            }
            disabled={disabled}
          >
            {valueContent}

            <span className={styles.chevron}>
              <IconChevronDown size={size === "large" ? 20 : 16} />
            </span>
          </Box>

          {typeof error === "string" && (
            <Box className={styles.errorMessage}>
              <IconAlertOctagon size={16} />
              <span>{error}</span>
            </Box>
          )}

          {hint && <Box className={styles.hint}>{hint}</Box>}
        </Box>
      </Popover.Target>

      <Popover.Dropdown
        p={0}
        style={{ background: "none", border: "none !important", width: "100%" }}
        className={styles.popoverDropdown}
      >
        <DropdownMenu
          width="100%"
          maxHeight={size === "large" ? 288 : 240}
          scrollable
          style={{ maxWidth: "100%" }}
        >
          {items.map((item) => {
            const left = isMulti ? (
              <span className={styles.checkCell}>
                <Checkbox
                  checked={isChecked(item.value)}
                  disabled={item.disabled || disabled}
                  radius="sm"
                  size={size === "large" ? "sm" : "xs"}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleMulti(item.value);
                  }}
                />
              </span>
            ) : null;

            return (
              <DropdownRow
                key={item.value}
                left={left}
                title={item.label}
                active={isChecked(item.value)}
                disabled={item.disabled}
                danger={item.danger}
                style={{ width: "100%" }}
                onClick={() => {
                  if (isMulti) {
                    toggleMulti(item.value);
                    return;
                  }

                  toggleSingle(item.value);
                }}
              />
            );
          })}
        </DropdownMenu>
      </Popover.Dropdown>
    </Popover>
  );
}
